import { LeadEmailBrandAssets } from '../data/types';

export const DEFAULT_HOMECOOK_LOGO_URL =
  'https://homecookapp.com/static/media/homecook_logo.1ca10013af71605db8e8.png';

export const DEFAULT_LEAD_EMAIL_BRAND_ASSETS: LeadEmailBrandAssets = {
  logo_url: DEFAULT_HOMECOOK_LOGO_URL,
  hero_image_url: 'https://placehold.co/600x240/CFE8EF/013847?text=Hero+image',
  feature_image_url: 'https://placehold.co/480x200/EFF8FA/013847?text=Feature',
  cta_image_url: 'https://placehold.co/320x120/013847/FFFFFF?text=CTA',
};

export type LeadEmailResolveLead = {
  first_name?: string;
  business_name?: string;
  city?: string;
};

export type ResolveLeadEmailHtmlOptions = {
  lead?: LeadEmailResolveLead | null;
  brandAssets?: LeadEmailBrandAssets | null;
  previewSample?: boolean;
  stripDuplicateFooter?: boolean;
};

const BRAND_TOKEN_KEYS = ['logo_url', 'hero_image_url', 'feature_image_url', 'cta_image_url'] as const;

type LeadEmailBrandImageKey = (typeof BRAND_TOKEN_KEYS)[number];

const ALT_TO_TOKEN: Record<string, LeadEmailBrandImageKey> = {
  logo: 'logo_url',
  'homecook logo': 'logo_url',
  hero: 'hero_image_url',
  'hero image': 'hero_image_url',
  feature: 'feature_image_url',
  cta: 'cta_image_url',
};

/** Merge brand asset defaults with overrides. */
export const mergeLeadEmailBrandAssets = (
  overrides?: LeadEmailBrandAssets | null
): LeadEmailBrandAssets => ({
  ...DEFAULT_LEAD_EMAIL_BRAND_ASSETS,
  ...(overrides ?? {}),
});

const isInvalidImageSrc = (src: string): boolean => {
  const trimmed = (src || '').trim();
  if (!trimmed || trimmed === '#' || trimmed === 'about:blank') return true;
  if (trimmed.startsWith('{{') && trimmed.endsWith('}}')) return false;
  if (/^data:/i.test(trimmed)) return false;
  try {
    const url = new URL(trimmed, 'https://homecookapp.com');
    if (!url.protocol.startsWith('http')) return true;
  } catch {
    return true;
  }
  return false;
};

const tokenFromAlt = (alt: string): LeadEmailBrandImageKey | null => {
  const normalized = alt.trim().toLowerCase();
  if (ALT_TO_TOKEN[normalized]) return ALT_TO_TOKEN[normalized];
  for (const key of BRAND_TOKEN_KEYS) {
    if (normalized.includes(key.replace(/_/g, ' ')) || normalized.includes(key.replace('_url', ''))) {
      return key;
    }
  }
  return null;
};

/** Repair broken img tags and map alt text to brand asset URLs. */
export const repairLeadEmailImages = (
  html: string,
  brandAssets: LeadEmailBrandAssets
): string => {
  if (!html) return html;

  return html.replace(/<img\b([^>]*?)>/gi, (match, attrs) => {
    const srcMatch = attrs.match(/\bsrc=["']([^"']*)["']/i);
    const altMatch = attrs.match(/\balt=["']([^"']*)["']/i);
    let src = srcMatch?.[1] ?? '';
    const alt = altMatch?.[1] ?? '';

    if (src.startsWith('{{') && src.endsWith('}}')) {
      return match;
    }

    const tokenFromSrc = BRAND_TOKEN_KEYS.find((key) => src.includes(`{{${key}}}`));
    if (tokenFromSrc) {
      return match;
    }

    if (isInvalidImageSrc(src)) {
      const tokenKey = tokenFromAlt(alt) ?? 'hero_image_url';
      const resolved = brandAssets[tokenKey] || DEFAULT_LEAD_EMAIL_BRAND_ASSETS[tokenKey];
      if (srcMatch) {
        return match.replace(srcMatch[0], `src="${resolved}"`);
      }
      return `<img src="${resolved}"${attrs}>`;
    }

    return match;
  });
};

/** Remove duplicate copyright footers when the HomeCook wrapper adds one. */
export const stripDuplicateLeadEmailFooter = (html: string): string => {
  if (!html) return html;
  const footerPattern = /©\s*\d{4}\s*HomeCook[^<]*/gi;
  const matches = html.match(footerPattern);
  if (!matches || matches.length <= 1) return html;
  let seen = 0;
  return html.replace(footerPattern, (segment) => {
    seen += 1;
    return seen === 1 ? segment : '';
  });
};

/** Resolve merge tags, brand tokens, and repair broken images in campaign email HTML. */
export const resolveLeadEmailHtml = (
  html: string,
  options: ResolveLeadEmailHtmlOptions = {}
): string => {
  if (!html) return html;

  const brandAssets = mergeLeadEmailBrandAssets(options.brandAssets);
  const lead = options.lead ?? {};
  const firstName = (lead.first_name || '').trim() || (options.previewSample ? 'there' : 'there');
  const businessName = (lead.business_name || '').trim() || (options.previewSample ? 'your business' : '');
  const city = (lead.city || '').trim() || (options.previewSample ? 'your city' : '');

  let resolved = html
    .replace(/\{\{first_name\}\}/g, firstName)
    .replace(/\{\{business_name\}\}/g, businessName)
    .replace(/\{\{city\}\}/g, city);

  for (const key of BRAND_TOKEN_KEYS) {
    const token = `{{${key}}}`;
    const value = brandAssets[key] || DEFAULT_LEAD_EMAIL_BRAND_ASSETS[key] || '';
    resolved = resolved.split(token).join(value);
  }

  resolved = repairLeadEmailImages(resolved, brandAssets);

  if (options.stripDuplicateFooter) {
    resolved = stripDuplicateLeadEmailFooter(resolved);
  }

  return resolved;
};

/** Normalize AI-generated campaign HTML to use brand tokens instead of broken external images. */
export const normalizeAiCampaignHtml = (html: string): string => {
  if (!html) return html;

  let normalized = html.replace(/<img\b([^>]*?)>/gi, (match, attrs) => {
    const srcMatch = attrs.match(/\bsrc=["']([^"']*)["']/i);
    const altMatch = attrs.match(/\balt=["']([^"']*)["']/i);
    const src = srcMatch?.[1] ?? '';
    const alt = altMatch?.[1] ?? '';

    if (src.startsWith('{{') && src.endsWith('}}')) {
      return match;
    }

    const tokenKey = tokenFromAlt(alt) ?? (/\blogo\b/i.test(alt) ? 'logo_url' : 'hero_image_url');
    const token = `{{${tokenKey}}}`;

    if (!src || isInvalidImageSrc(src) || /unsplash|pexels|placeholder|example\.com/i.test(src)) {
      if (srcMatch) {
        return match.replace(srcMatch[0], `src="${token}"`);
      }
      return `<img src="${token}"${attrs}>`;
    }

    return match;
  });

  normalized = stripDuplicateLeadEmailFooter(normalized);
  return normalized;
};
